/**
 * OpenAI (ChatGPT ads) pixel — the theme half.
 *
 * Two halves live in different execution contexts on purpose:
 *
 *   - THIS FILE runs on storefront pages, where the SDK is an ordinary script
 *     on an ordinary page. That is the only place it can see the real landing
 *     URL and the real cookie jar, so it is where the `oppref` click id gets
 *     caught off the query string. It is persisted TWICE, on purpose:
 *       · the `__oppref` cookie, written by the SDK — what carries the id to
 *         the checkout document; and
 *       · the `_oppref` CART ATTRIBUTE, written here — what carries it onto the
 *         ORDER, so a later server-side replay through the Conversions API can
 *         read it long after the browser is gone. A cookie cannot do that.
 *   - A Shopify CUSTOM PIXEL (Settings → Customer events; reference copy at
 *     library/analytics/openai-custom-pixel.js) runs on checkout and the
 *     thank-you page, where no theme code runs at all under Checkout
 *     Extensibility. It fires the conversion through the SDK, which finds the
 *     `__oppref` cookie by itself from inside the sandbox. It does NOT pass an
 *     oppref explicitly — the transport that accepted one is unusable from a
 *     pixel, and the `_oppref` cart attribute is read there for diagnostics and
 *     a future server-side replay only.
 *
 * The custom pixel cannot do this half. Its Lax sandbox reads the sandbox's own
 * URL rather than the top frame's, so `oppref` is never in the query string it
 * sees, and `document.cookie` there is not the storefront's cookie jar.
 *
 * SCOPE: new subscription purchases, which redirect to Shopify checkout on the
 * same origin (plans.js), so the cookie is present on the checkout document.
 * The reactivation "activate" path is deliberately out of scope — it calls
 * Stately middleware (→ Recharge) directly, so no Shopify order exists for a
 * custom pixel to fire on, cookie or no cookie. See plans.js
 * fireWinbackSuccessEvent and library/analytics/OpenAI-Pixel.md.
 *
 * WHY NOT <head>. The vendor's head snippet writes the cookie only after the
 * consent API answers (async) and the SDK downloads from a third-party host
 * (async) — later than this deferred module even starts. Head placement buys no
 * ordering guarantee here, so it would be clutter with no benefit. Contrast
 * consent-google.liquid and convert-bucketing.liquid, which are inline because
 * something later in the same document genuinely reads what they set.
 *
 * CONSENT. A marketing pixel, so the gate is marketingAllowed() &&
 * saleOfDataAllowed() — the same authority and the same sale-implies-everything
 * rule as consent-google.liquid and sm-track.js. Keep the three in agreement;
 * two layers disagreeing about what an opt-out means is worse than either rule
 * alone. See library/analytics/Consent-Architecture.md §5.4.
 *
 * SHAPE. Everything consent touches goes through resolve(): derive the desired
 * state, compare it against the state last applied, apply the difference. Boot,
 * a consent event and a tab focus just call it. See OpenAI-Pixel.md §2.
 */

(function () {
  const config = (window.statelyVars && window.statelyVars.openaiPixel) || {};
  if (!config.enabled) return;

  const SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
  const CLICK_PARAM = "oppref";
  const CLICK_COOKIE = "__oppref";
  const CART_ATTR = "_oppref";
  const MIRROR_COOKIE = "sm_consent_state";
  const INPUT_NAME = "attributes[" + CART_ATTR + "]";
  const CART_FORM = 'form[action*="/cart/add"]';

  // How long to wait for the SDK to write __oppref before reporting the capture
  // unconfirmed. Generous enough to cover a slow CDN on a cold connection, short
  // enough to resolve before a shopper on an ad landing page navigates away.
  const CAPTURE_CONFIRM_MS = 4000;
  const CAPTURE_POLL_MS = 250;

  // The theme editor has no real cart worth mutating. Read once: designMode is
  // set by content_for_header, which has parsed before this deferred module.
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /**
   * The last state resolve() acted on, or null before the first trigger: the
   * answer, the click id it applies to, and handles on what that decision left
   * behind. Asking it, rather than a separate latch, is how the async cart path
   * notices a denial that landed while it was awaiting.
   */
  let applied = null;

  // A per-ad-click heartbeat, so it must survive an opt-out / opt-in round trip
  // without emitting twice. Per-pageview, not consent state — hence not on
  // `applied`.
  let markerEmitted = false;

  function debug(message) {
    if (window.smDebug) console.log("🤖 openai-pixel", message);
  }

  // Exact name match, not a substring scan: `other___oppref=…` would satisfy
  // an indexOf check. Same reasoning as cookieValue() in consent-google.liquid.
  function readCookie(name) {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const parts = cookie.split("=");
      if (parts[0].trim() !== name) continue;
      return parts.slice(1).join("=").trim();
    }
    return null;
  }

  /**
   * The marketing bit of consent-google.liquid's `sm_consent_state` mirror, and
   * the one consent signal readable SYNCHRONOUSLY — see marketingIsAllowed()
   * for when it is allowed to answer. Bit 0 already folds in sale-of-data (see
   * reconcile() there), so it alone answers this pixel's question. Anything not
   * the documented three-character shape is treated as absent — fail open,
   * matching every other gate in the theme.
   *
   * The predecessor cookie (`sm_consent_optout`) is deliberately NOT read here:
   * it never reached production, consent-google.liquid migrates it on this same
   * pageview, and reading it would put a third copy of the mirror format in the
   * theme for a population of approximately nobody.
   */
  function mirrorDeniesMarketing() {
    const value = readCookie(MIRROR_COOKIE);
    return value !== null && /^[01]{3}$/.test(value) && value.charAt(0) === "0";
  }

  // Each accessor is guarded individually so a missing or throwing method
  // cannot take the whole gate down — the same posture as consent-google's
  // allowed() and sm-track's refreshConsent(), and the same default.
  function allowed(privacy, method) {
    try {
      return (
        typeof privacy[method] !== "function" || privacy[method]() !== false
      );
    } catch (err) {
      return true;
    }
  }

  /**
   * Only an EXPLICIT denial blocks. An absent or throwing API fails open, per
   * the store's CCPA opt-out model (Consent-Architecture.md §1): a gate that
   * cannot reach its authority costs compliance, not data.
   */
  function marketingDenied() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return false;
    return !(
      allowed(privacy, "saleOfDataAllowed") &&
      allowed(privacy, "marketingAllowed")
    );
  }

  /**
   * The consent answer, and how much the cached mirror is worth — the only
   * thing that differs between triggers.
   *
   * `cached` is the boot read, taken before consent-tracking-api has been asked
   * for. A mirrored denial IS the answer there: it is what stops a returning
   * opted-out visitor from reaching the download at all, and no authority has
   * been consulted to contradict it.
   *
   * On every later trigger the authority HAS been asked, and a present one
   * outranks the mirror — consent-google.liquid rewrites that cookie, so an
   * opt-in must be able to clear a stale denial. An absent one does not:
   * marketingDenied() fails open, and fail-open is the house rule for having NO
   * evidence. A mirrored denial is evidence, so it holds until the authority
   * contradicts it — otherwise a focus arriving before any other consumer has
   * loaded the feature would download the SDK for someone who opted out.
   */
  function marketingIsAllowed(cached) {
    if (marketingDenied()) return false;
    if (!mirrorDeniesMarketing()) return true;
    return !cached && Boolean(window.Shopify && window.Shopify.customerPrivacy);
  }

  /**
   * The click id, from the landing URL if this IS the landing page, otherwise
   * from the cookie the SDK persisted on an earlier one.
   *
   * URL first because it is unambiguously the raw identifier; the cookie is the
   * SDK's own storage, passed through verbatim. If OpenAI ever wraps it (JSON,
   * encoding) the cart attribute would carry that wrapper — but so would the
   * custom pixel's own cookie read, so no parsing here.
   */
  function resolveClickId() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(
        CLICK_PARAM
      );
      // `fromUrl` is what distinguishes an ad LANDING from any later pageview
      // in the same session, both of which carry the cookie. The marker keys on
      // it; the cart write does not.
      if (fromUrl) return { id: fromUrl, fromUrl: true };
    } catch (err) {
      // Malformed query string — fall through to the cookie.
    }
    const fromCookie = readCookie(CLICK_COOKIE);
    return fromCookie ? { id: fromCookie, fromUrl: false } : null;
  }

  /** What the page should look like right now, derived fresh every time. */
  function desiredState(cached) {
    return {
      allowed: marketingIsAllowed(cached),
      clickId: resolveClickId(),
      // Carried: init is a fact about the document, not about the current
      // answer — without this a re-grant would queue a second one.
      sdkInitialized: Boolean(applied && applied.sdkInitialized),
      // Filled in as the transition is applied, undone from the one it replaces.
      observer: null,
      cartOp: null,
    };
  }

  /** Unchanged when both the answer and the id it applies to are. */
  function same(a, b) {
    return Boolean(
      a &&
        a.allowed === b.allowed &&
        (a.clickId && a.clickId.id) === (b.clickId && b.clickId.id) &&
        (a.clickId && a.clickId.fromUrl) === (b.clickId && b.clickId.fromUrl)
    );
  }

  /**
   * The only caller of enable() and disable(), and the only thing any trigger
   * has to do. A trigger that changes nothing costs two cookie reads and one
   * consent read.
   */
  async function resolve(reason) {
    const next = desiredState(reason === "boot");
    if (same(applied, next)) return;
    const previous = applied;
    applied = next;
    debug((next.allowed ? "allowed" : "blocked") + " (" + reason + ")");
    if (next.allowed) enable(next, previous);
    else await disable(next, previous);
  }

  // Re-checked after every await on the cart path, never assumed from the
  // consent that was true when the work started.
  function stillAllowed() {
    return Boolean(applied && applied.allowed);
  }

  /**
   * Grant, and load the SDK if this document has not already.
   *
   * The grant is explicit and unconditional, never left to the SDK's
   * granted-by-default, because the SDK PERSISTS a denial: once
   * oaiq('consent', false) has been called for a visitor it finds that stored
   * denial on every later visit and stays suppressed. An explicit true is what
   * releases a shopper who opted out and then opted back in — the one consent
   * failure mode this store's model does NOT catch, because it errs toward
   * privacy rather than toward data. Same reason an already-initialized pixel
   * re-grants here rather than returning silently: disable() queued that
   * consent(false) and nothing else would walk it back within one document.
   * Shopify's opt-out page reloads on submit, so that is narrow — but an app
   * calling setTrackingConsent without a reload reaches it.
   */
  function grantSdk(state) {
    if (state.sdkInitialized) {
      if (window.oaiq) window.oaiq("consent", true);
      return;
    }
    state.sdkInitialized = true;

    // Guard on window.oaiq: another install (an app embed, a GTM tag) may
    // already own the queue, and re-stubbing it would discard whatever it has
    // buffered.
    if (!window.oaiq) {
      const queue = function () {
        queue.q.push(arguments);
      };
      queue.q = [];
      window.oaiq = queue;

      const script = document.createElement("script");
      script.async = true;
      script.src = SDK_URL;
      document.head.appendChild(script);
    }

    // Consent BEFORE init, per OpenAI's docs.
    window.oaiq("consent", true);
    window.oaiq("init", { pixelId: config.pixelId });
    debug("init " + config.pixelId);
  }

  /**
   * Everything a grant implies: the SDK, the cart attribute, the hidden inputs,
   * the observer, the marker. Re-runnable in full, because disable() tore all
   * of it down and a shopper who consents again should not spend the rest of
   * the session without the attribution they just re-allowed.
   */
  function enable(state, previous) {
    grantSdk(state);
    if (!state.clickId) return;

    if (!designMode) {
      state.cartOp = syncCartAttribute(state.clickId.id, previous);
      injectHiddenInputs(state.clickId.id);
      // An earlier grant's observer holds a stale click id in its closure.
      if (previous && previous.observer) previous.observer.disconnect();
      state.observer = watchForNewCartForms(state.clickId.id);
    }

    // The marker fires ONLY on the landing page, never on the pageviews that
    // follow it. Both carry the cookie, so keying on the resolved id would
    // count sessions rather than ad clicks, with a page_type describing
    // wherever the shopper wandered instead of where the ad delivered them —
    // worse than no marker, because it still looks like data.
    if (state.clickId.fromUrl) {
      confirmSdkCapture(state.clickId.id);
      debug("captured " + CLICK_PARAM + " from the landing URL");
    } else {
      debug("carried " + CLICK_PARAM + " from the cookie");
    }
  }

  /**
   * Everything a denial implies — the whole of it, in one place.
   *
   * Stopping the SDK is only half. `_oppref` is a marketing attribution
   * identifier, so after a denial it must stop being injected, must not ride a
   * form submission from inputs already in the DOM, and must not stay on the
   * cart, from where it reaches the ORDER.
   *
   * The clear is ordered behind the previous transition's cart request, like
   * every other one — see afterPreviousCartOp().
   */
  async function disable(state, previous) {
    if (window.oaiq) window.oaiq("consent", false);
    if (previous && previous.observer) previous.observer.disconnect();
    stripHiddenInputs();
    if (designMode) return;
    state.cartOp = clearCartAttribute(previous);
    await state.cartOp;
  }

  /**
   * Cart requests are serialized across transitions: each waits for the
   * previous transition's request to land before reading the cart. A request
   * already on the wire cannot be recalled, so whichever lands last wins, and
   * neither order is safe on its own. Clear first and an opt-out reads the cart
   * before the write it must undo lands, finds nothing to remove, and lets that
   * write commit the identifier AFTER the denial. Write first and an opt-in
   * reads before the clear lands, sees the id still there, skips its own write,
   * and the clear then empties it. `applied` stops a request not yet issued;
   * this handles the ones already gone.
   */
  async function afterPreviousCartOp(previous) {
    await Promise.resolve(previous && previous.cartOp).catch(function () {});
  }

  /** The `_oppref` on the cart now; throws if the cart is unreadable. */
  async function readCartAttribute() {
    const response = await fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("HTTP " + response.status);
    const cart = await response.json();
    return cart && cart.attributes ? cart.attributes[CART_ATTR] : undefined;
  }

  /** Write `_oppref`; an empty string is how Shopify removes an attribute. */
  async function writeCartAttribute(value) {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ attributes: { [CART_ATTR]: value } }),
    });
    // fetch resolves for 4xx and 5xx, so without this check the clear below
    // would report the identifier as removed while it is still on the cart —
    // the one claim in this path that must not be optimistic.
    if (!response.ok) throw new Error("HTTP " + response.status);
  }

  /**
   * Persist the click id as a cart attribute so it rides the order.
   *
   * Underscore-prefixed by convention. NOTE: that the prefix hides a CART
   * ATTRIBUTE from order admin and customer emails is UNVERIFIED — Shopify
   * documents `_` for line-item PROPERTIES, and this is not one — so treat the
   * id as potentially visible on the order. Cosmetic rather than a privacy
   * question: `oppref` is an ad click id the shopper arrived with in their own
   * URL, is not PII, and already goes to OpenAI. If the visibility does turn
   * out to matter, check a live order carrying Elevar's `_elevar__*` attributes
   * to see how Shopify renders them before renaming anything — the key is
   * referenced by the Admin pixel too.
   *
   * Idempotent, and last-click-wins: only writes when the stored value differs,
   * so navigating with a stale cart does not churn requests, and arriving via a
   * second ad overwrites the first — the same rule ab-test.js applies to
   * `_ab_<name>`.
   */
  async function syncCartAttribute(clickId, previous) {
    try {
      await afterPreviousCartOp(previous);
      if ((await readCartAttribute()) === clickId) return;

      // Re-checked AFTER the await: consent can be withdrawn while the GET is
      // in flight, and this is the last point before the id is persisted.
      if (!stillAllowed()) {
        debug("consent withdrawn mid-write; cart attribute not written");
        return;
      }

      await writeCartAttribute(clickId);
      debug("cart attribute written");
    } catch (err) {
      // Non-fatal. The hidden inputs below still carry the first add-to-cart,
      // the cookie still carries checkout, and the next pageview retries.
      debug("cart attribute write failed");
    }
  }

  /**
   * Remove `_oppref` from the cart, if it is there.
   *
   * Reads first, so it is idempotent and cheap to repeat: a denied visitor's
   * every pageview calls this, and once the attribute is gone it costs one GET
   * and no write. That repetition is the point — a single clear at opt-out time
   * can fail, or the page can close mid-request, and the identifier would then
   * ride to the ORDER despite the denial.
   */
  async function clearCartAttribute(previous) {
    try {
      await afterPreviousCartOp(previous);
      if (!(await readCartAttribute())) return;
      await writeCartAttribute("");
      debug("cart attribute cleared after opt-out");
    } catch (err) {
      debug("could not clear the cart attribute; will retry next pageview");
    }
  }

  /**
   * Carry the attribute on the add-to-cart form itself, so the FIRST add
   * carries it even if the async /cart/update.js has not landed, and so it
   * survives a cart Shopify recreates in between.
   */
  function injectHiddenInputs(clickId) {
    document.querySelectorAll(CART_FORM).forEach(function (form) {
      const existing = form.querySelector('input[name="' + INPUT_NAME + '"]');
      if (existing) {
        if (existing.value !== clickId) existing.value = clickId;
        return;
      }
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = INPUT_NAME;
      input.value = clickId;
      form.appendChild(input);
    });
  }

  function stripHiddenInputs() {
    document
      .querySelectorAll('input[name="' + INPUT_NAME + '"]')
      .forEach(function (input) {
        if (input.parentNode) input.parentNode.removeChild(input);
      });
  }

  // Cart forms are injected dynamically (quick add, mini PDP modal), so a
  // one-shot scan would miss them. Only ever observed on a rare ad landing.
  function watchForNewCartForms(clickId) {
    const observer = new MutationObserver(function (mutations) {
      // disable() disconnects, which drops any queued records with it — this
      // is belt and braces on a privacy path, not what stops the injection.
      if (!stillAllowed()) return;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          const isForm = node.matches && node.matches(CART_FORM);
          const hasForm = node.querySelector && node.querySelector(CART_FORM);
          if (isForm || hasForm) {
            injectHiddenInputs(clickId);
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  }

  /**
   * Emit the marker once the SDK has actually stored the click id — or once it
   * is clear it has not.
   *
   * The marker is the reason to bother: a broken theme half is INVISIBLE
   * downstream. The custom pixel still fires order_created with correct
   * revenue, just with no click id. And queuing oaiq('init') proves only that
   * we asked — the SDK still has to download from a third-party CDN and write
   * `__oppref`, the step that actually establishes attribution. An ad blocker
   * or a blocked CDN kills it while every earlier step still looks healthy.
   *
   * So the marker carries the outcome rather than merely its own existence:
   *
   *   sdk_state: "confirmed"    the cookie now holds this click id
   *   sdk_state: "unconfirmed"  it does not, within CAPTURE_CONFIRM_MS
   *
   * Alarm on `sdk_state == unconfirmed` and you learn that clicks arrived and
   * attribution broke — which absence alone cannot tell you apart from a day
   * with no ad clicks at all.
   *
   * Presence only, never the value: `oppref` is an attribution identifier and
   * has no business in the dataLayer. page_type comes free from smTrack, which
   * answers the useful follow-up — which landing pages ad clicks arrive on.
   *
   * Compared against the id from the URL, not merely for the cookie's presence:
   * a returning shopper clicking a SECOND ad already carries the first click's
   * cookie, and reporting that as confirmation would hide the SDK failing to
   * take the new one.
   */
  function confirmSdkCapture(clickId) {
    if (markerEmitted) return;
    const deadline = Date.now() + CAPTURE_CONFIRM_MS;

    const emit = (state) => {
      // The poll can outlive the consent that started it: it runs for up to
      // CAPTURE_CONFIRM_MS, and a denial collected inside that window would
      // otherwise still get a marker pushed afterwards. smTrack's own gate does
      // not cross tabs either, so it cannot be relied on here. `markerEmitted`
      // is deliberately NOT set: nothing was emitted, so a later opt-in
      // re-running enable() is free to start a fresh capture.
      if (markerEmitted || !stillAllowed()) return;
      markerEmitted = true;
      const trackOpenaiPixel = window.smTrack;
      if (typeof trackOpenaiPixel === "function") {
        trackOpenaiPixel("openai_click_captured", { sdk_state: state });
      }
      debug("sdk capture " + state);
    };

    const poll = () => {
      if (readCookie(CLICK_COOKIE) === clickId) {
        emit("confirmed");
        return;
      }
      if (Date.now() >= deadline) {
        emit("unconfirmed");
        return;
      }
      setTimeout(poll, CAPTURE_POLL_MS);
    };

    poll();
  }

  /**
   * Boot. The mirror is the only SYNCHRONOUS consent signal, so it decides
   * whether the authority is worth waiting for: a mirrored denial resolves
   * immediately — no download, and the stale cart attribute retried — while
   * anything else defers to consent-tracking-api, which is the authority and
   * may contradict the cache.
   *
   * core.js is deferred, so content_for_header — where Shopify.loadFeatures is
   * defined — has already parsed by the time this runs. That lets this skip the
   * retry poll consent-google.liquid needs in <head>, and makes the
   * no-loadFeatures path effectively unreachable in a real page; it still has
   * to fail toward loading, like every other gate here.
   */
  const loadFeatures = window.Shopify && window.Shopify.loadFeatures;
  if (!mirrorDeniesMarketing() && typeof loadFeatures === "function") {
    loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function () {
        // The error argument is intentionally ignored: other consumers
        // (consent-google, the Clarity embed) request the same feature, so
        // customerPrivacy can be present even when this request failed.
        // marketingDenied() re-reads the authority either way.
        resolve("load_features");
      }
    );
  } else {
    resolve("boot");
  }

  // Catches an opt-out (or opt-in) made mid-session on the privacy page.
  // Registered unconditionally so a change is honored even when the initial
  // resolve declined to load.
  document.addEventListener("visitorConsentCollected", function () {
    resolve("visitor_consent_collected");
  });

  // visitorConsentCollected is dispatched on the document where consent was
  // collected and does NOT cross tabs (Consent-Architecture.md §8). A tab left
  // open while the shopper opts out elsewhere would otherwise keep the SDK
  // granted and the cart-persistence path live for the rest of its life — the
  // longest-lived version of exactly the leak disable() exists to stop.
  //
  // Re-reading on focus closes it, the same way consent-google.liquid does: the
  // Customer Privacy API reads Shopify's shared `_tracking_consent` cookie, so
  // the other tab's decision is visible here. An ordinary tab switch stops at
  // resolve()'s comparison against what it last applied, so it never re-issues
  // GET /cart.js.
  //
  // Until boot has resolved there is nothing to compare against, and a focus
  // must not fill that gap: the authority is still loading, and
  // marketingIsAllowed() fails open on an absent API — which is why boot waits
  // for the loadFeatures callback instead of resolving. A link opened in a
  // background tab lands here on its first foregrounding, and without this
  // guard it would download the SDK ahead of the stored denial the callback is
  // about to expose. A focus carries no consent information of its own, so
  // deferring to that callback costs nothing.
  document.addEventListener("visibilitychange", function () {
    if (document.hidden || !applied) return;
    resolve("visibility_change");
  });
})();
